import { queryId } from '@ably/ui/core/dom-query';

declare global {
  interface Window {
    hsConversationsSettings?: {
      loadImmediately: boolean;
    };
    HubSpotConversations?: {
      widget: {
        load: () => void;
        open: () => void;
        remove: () => void;
        status: () => { loaded: boolean };
      };
      on: (event: string, callback: (eventPayload: Record<string, unknown> | undefined) => void) => void;
      off: (event: string, callback: (() => void) | null | undefined) => void;
    };
  }
}

const setInnerTextPreserveIcon = (el: HTMLElement, text: string) => {
  const icon = el.querySelector('svg');

  if (icon) {
    el.innerHTML = '';
    el.appendChild(icon);
    el.appendChild(document.createTextNode(text));
  } else {
    el.innerText = text;
  }
};

const enableBtn = (el: HTMLButtonElement, text: string) => {
  el.disabled = false;
  setInnerTextPreserveIcon(el, text);
};

const disableBtn = (el: HTMLButtonElement, text: string) => {
  el.disabled = true;
  setInnerTextPreserveIcon(el, text);
};

const noop = () => {};

/*
  This function is used to toggle the HubSpot chat widget.
  It is used to enable/disable the chat button and to open the chat widget.

  Params:
  - container           the container of the chat button
  - buttonDataId        the data-id of the chat button itself, defaults to 'open-chat-widget'
*/
const toggleChatWidget = (params: { buttonDataId?: string; container: HTMLElement }) => {
  const { buttonDataId = 'open-chat-widget', container } = params;

  const chatButton = queryId(buttonDataId, container) as HTMLButtonElement;
  if (!chatButton) return noop;

  const textEnabled = chatButton.dataset.enabledLabel as string;
  const textDisabled = chatButton.dataset.disabledLabel as string;

  const clickHandler = (e: Event) => {
    e.preventDefault();

    const widget = window?.HubSpotConversations?.widget;

    if (!widget?.status().loaded) {
      window?.HubSpotConversations?.widget.load();
    }

    window?.HubSpotConversations?.widget.open();
  };

  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const waitForScript = (delay: number, retryCount: number = 30) => {
    const widget = window?.HubSpotConversations?.widget;

    // If the chat is set to be hidden out of hours this will return null
    const iframe = document.querySelector('#hubspot-messages-iframe-container');

    const widgetIsHidden = !window.hsConversationsSettings?.loadImmediately;

    if (widget && (iframe || widgetIsHidden)) {
      chatButton.addEventListener('click', clickHandler);
      enableBtn(chatButton, textEnabled);
    } else if (retryCount > 0) {
      // Exponential backoff to handle slower connections
      const backoffDelay = Math.min(delay === 0 ? 50 : delay * 2, 5000);
      timeoutId = setTimeout(() => waitForScript(backoffDelay, retryCount - 1), backoffDelay);
    }
  };

  disableBtn(chatButton, textDisabled);
  waitForScript(0);

  return () => {
    disableBtn(chatButton, textDisabled);

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (clickHandler) {
      chatButton.removeEventListener('click', clickHandler);
    }
  };
};

export default toggleChatWidget;
